export const HRS_PRICE_MULTIPLIER = 1.5 * 1.09;
export const HRS_SXR_SOLID_BRACE_PAIR_MSRP_USD = 2315;
export const HRS_SXR_SOLID_BRACE_CLEARANCE_IN = 0.38;
export const HRS_SXR_EXTENDED_SPACING_POST_COUNT = 6;
export const HRS_SXR_EXTENDED_SPACING_POST_MSRP_USD = {
  12: 30,
  14: 50,
  16: 75,
} as const;

export type HrsSxrExtendedSpacing =
  keyof typeof HRS_SXR_EXTENDED_SPACING_POST_MSRP_USD;
export type HrsSxrExtendedSpacingCounts = Partial<
  Record<HrsSxrExtendedSpacing, number>
>;

export type HrsSeries = "EXR" | "SXR" | "VXR";
export type HrsFinish = "black" | "silver";
export type HrsBaseSeries = "E1X" | "R3X" | "S3" | "M3X2";

export type HrsFrameOption = {
  series: HrsSeries;
  sizeCode: string;
  levels: number;
  sku: string;
  msrpUsd: number;
  standardSpacing: number[];
};

export type HrsBaseOption = {
  series: HrsBaseSeries;
  sizeCode: string;
  sku: string;
  widthIn: number;
  depthIn: number;
  msrpUsd: number;
};

export type HrsEquipmentDimensions = {
  widthIn: number | null;
  depthIn: number | null;
  heightIn: number | null;
  weightKg: number | null;
};

export type HrsEquipmentLookupStatus = "complete" | "partial" | "empty";

export function classifyHrsEquipmentLookup(
  found: HrsEquipmentDimensions
): HrsEquipmentLookupStatus {
  const foundCount = Object.values(found).filter(value => value != null).length;
  if (foundCount === 4) return "complete";
  if (foundCount > 0) return "partial";
  return "empty";
}

/** Keep staff-entered values when an enrichment response omits a field. */
export function mergeHrsEquipmentDimensions(
  current: HrsEquipmentDimensions,
  found: HrsEquipmentDimensions
): HrsEquipmentDimensions {
  return {
    widthIn: found.widthIn ?? current.widthIn,
    depthIn: found.depthIn ?? current.depthIn,
    heightIn: found.heightIn ?? current.heightIn,
    weightKg: found.weightKg ?? current.weightKg,
  };
}

export type HrsFitResult = {
  status: "unchecked" | "fits" | "review" | "does_not_fit";
  issues: string[];
  notes: string[];
};

const framePrices: Record<HrsSeries, Record<string, number[]>> = {
  EXR: {
    "1921": [2695, 4845, 6995, 9145, 11295],
    "1719": [2695, 4845, 6995, 9145, 11295],
  },
  SXR: {
    "1921": [3350, 6045, 8740, 11435, 14130],
    "1719": [3300, 5995, 8690, 11385, 14080],
  },
  VXR: {
    "1921": [8795, 16995, 25195, 33395, 41595],
  },
};

export const HRS_FRAME_OPTIONS: HrsFrameOption[] = Object.entries(
  framePrices
).flatMap(([series, sizes]) =>
  Object.entries(sizes).flatMap(([sizeCode, prices]) =>
    prices.map((msrpUsd, index) => {
      const levels = index + 1;
      return {
        series: series as HrsSeries,
        sizeCode,
        levels,
        sku: `${series}-${sizeCode}-${levels}V`,
        msrpUsd,
        standardSpacing:
          series === "SXR"
            ? [2, 4, 6, 8, 10, 12, 14, 16]
            : series === "EXR"
              ? [4, 6, 8, 10, 12]
              : [8],
      };
    })
  )
);

export const HRS_BASE_OPTIONS: HrsBaseOption[] = [
  ["E1X", "2123", 23, 21, 2035],
  ["E1X", "1925", 25, 19, 2035],
  ["E1X", "1923", 23, 19, 1940],
  ["E1X", "1921", 21, 19, 1885],
  ["E1X", "1719", 19, 17, 1840],
  ["E1X", "1419", 19, 14, 1550],
  ["E1X", "9.517-2", 9.5, 17, 1425],
  ["E1X", "9.515-2", 9.5, 15, 1225],
  ["R3X", "1921", 21, 19, 2125],
  ["R3X", "1719", 19, 17, 2075],
  ["S3", "1921", 21, 19, 2850],
  ["S3", "1719", 19, 17, 2850],
  ["M3X2", "2123", 23, 21, 5295],
  ["M3X2", "1925", 25, 19, 5295],
  ["M3X2", "1923", 23, 19, 4945],
  ["M3X2", "1921", 21, 19, 4595],
  ["M3X2", "1719", 19, 17, 3895],
  ["M3X2", "1419", 19, 14, 3330],
].map(([series, sizeCode, widthIn, depthIn, msrpUsd]) => ({
  series: series as HrsBaseSeries,
  sizeCode: String(sizeCode),
  sku: `${series}-${sizeCode}`,
  widthIn: Number(widthIn),
  depthIn: Number(depthIn),
  msrpUsd: Number(msrpUsd),
}));

const EXR_INTEGRATED_FRAME_PRICES: Record<number, Record<number, number>> = {
  2: { 1: 4925, 2: 8995, 3: 13095, 4: 16995 },
  3: { 1: 6950, 2: 12925, 3: 18995, 4: 24995 },
};

const VXR_CENTER_PANEL_PRICES: Record<number, number> = {
  1: 6295,
  2: 13670,
  3: 21045,
  4: 28420,
  5: 35795,
};

export function findHrsFrame(
  series: HrsSeries,
  sizeCode: string,
  levels: number
) {
  return HRS_FRAME_OPTIONS.find(
    option =>
      option.series === series &&
      option.sizeCode === sizeCode &&
      option.levels === levels
  );
}

export function findHrsBase(series: HrsBaseSeries, sizeCode: string) {
  return HRS_BASE_OPTIONS.find(
    option => option.series === series && option.sizeCode === sizeCode
  );
}

export function calculateHrsConfigurationPrice(input: {
  frame: HrsFrameOption;
  bayCount: number;
  base?: HrsBaseOption | null;
  baseCount?: number;
  accessoriesMsrpUsd?: number;
}) {
  const frameMsrpUsd = input.frame.msrpUsd * input.bayCount;
  const baseMsrpUsd = (input.base?.msrpUsd ?? 0) * (input.baseCount ?? 0);
  const accessoriesMsrpUsd = input.accessoriesMsrpUsd ?? 0;
  const totalMsrpUsd = frameMsrpUsd + baseMsrpUsd + accessoriesMsrpUsd;
  return {
    frameMsrpUsd,
    baseMsrpUsd,
    accessoriesMsrpUsd,
    totalMsrpUsd,
    configuredPriceSgd: Math.round(totalMsrpUsd * HRS_PRICE_MULTIPLIER),
  };
}

export function calculateHrsJoinedConfigurationPrice(input: {
  series: HrsSeries;
  sizeCode: string;
  rackLevels: number[];
  base?: HrsBaseOption | null;
  /** Exact per-level bases. When present, these replace base × level count. */
  bases?: HrsBaseOption[];
  solidBracePairCount?: number;
  extendedSpacingCounts?: HrsSxrExtendedSpacingCounts;
}) {
  const { series, sizeCode, rackLevels, base } = input;
  const frames = rackLevels.map(levels =>
    findHrsFrame(series, sizeCode, levels)
  );
  if (frames.some(frame => !frame)) {
    return {
      customQuoteRequired: true,
      frameMsrpUsd: 0,
      baseMsrpUsd: 0,
      accessoriesMsrpUsd: 0,
      totalMsrpUsd: 0,
      configuredPriceSgd: null,
    };
  }

  let frameMsrpUsd = 0;
  let customQuoteRequired = false;
  if (series === "SXR" || rackLevels.length === 1) {
    frameMsrpUsd = frames.reduce((sum, frame) => sum + frame!.msrpUsd, 0);
  } else if (series === "EXR") {
    const allSameHeight = rackLevels.every(levels => levels === rackLevels[0]);
    const publishedPrice = allSameHeight
      ? EXR_INTEGRATED_FRAME_PRICES[rackLevels.length]?.[rackLevels[0]]
      : undefined;
    if (publishedPrice != null) frameMsrpUsd = publishedPrice;
    else {
      frameMsrpUsd = frames.reduce((sum, frame) => sum + frame!.msrpUsd, 0);
      customQuoteRequired = true;
    }
  } else {
    frameMsrpUsd = frames[0]!.msrpUsd;
    for (const levels of rackLevels.slice(1)) {
      const centerPanelPrice = VXR_CENTER_PANEL_PRICES[levels];
      if (centerPanelPrice == null) customQuoteRequired = true;
      else frameMsrpUsd += centerPanelPrice;
    }
  }

  const baseCount = base
    ? rackLevels.reduce((sum, levels) => sum + levels, 0)
    : 0;
  const baseMsrpUsd = input.bases
    ? input.bases.reduce((sum, selectedBase) => sum + selectedBase.msrpUsd, 0)
    : (base?.msrpUsd ?? 0) * baseCount;
  const solidBracePairCount =
    series === "SXR" ? Math.max(0, input.solidBracePairCount ?? 0) : 0;
  const extendedSpacingMsrpUsd =
    series === "SXR"
      ? (
          Object.entries(input.extendedSpacingCounts ?? {}) as Array<
            [`${HrsSxrExtendedSpacing}`, number]
          >
        ).reduce(
          (sum, [spacing, count]) =>
            sum +
            Math.max(0, count) *
              HRS_SXR_EXTENDED_SPACING_POST_COUNT *
              HRS_SXR_EXTENDED_SPACING_POST_MSRP_USD[
                Number(spacing) as HrsSxrExtendedSpacing
              ],
          0
        )
      : 0;
  const accessoriesMsrpUsd =
    solidBracePairCount * HRS_SXR_SOLID_BRACE_PAIR_MSRP_USD +
    extendedSpacingMsrpUsd;
  const totalMsrpUsd = frameMsrpUsd + baseMsrpUsd + accessoriesMsrpUsd;
  return {
    customQuoteRequired,
    frameMsrpUsd,
    baseMsrpUsd,
    accessoriesMsrpUsd,
    extendedSpacingMsrpUsd,
    totalMsrpUsd,
    configuredPriceSgd: customQuoteRequired
      ? null
      : Math.round(totalMsrpUsd * HRS_PRICE_MULTIPLIER),
  };
}

export function checkHrsEquipmentFit(input: {
  equipmentName?: string;
  dimensions: HrsEquipmentDimensions;
  base: Pick<HrsBaseOption, "widthIn" | "depthIn"> | null;
  clearHeightIn: number;
  ventilationAllowanceIn?: number;
}): HrsFitResult {
  const { dimensions, base, clearHeightIn } = input;
  const values = [
    dimensions.widthIn,
    dimensions.depthIn,
    dimensions.heightIn,
    dimensions.weightKg,
  ];
  if (!input.equipmentName?.trim() && values.every(value => value == null)) {
    return {
      status: "unchecked",
      issues: [],
      notes: ["Equipment not specified. Fit checks remain pending."],
    };
  }

  const issues: string[] = [];
  const notes: string[] = [];
  const roundedWidth =
    dimensions.widthIn == null ? null : Math.round(dimensions.widthIn);
  const roundedDepth =
    dimensions.depthIn == null ? null : Math.round(dimensions.depthIn);
  const roundedHeight =
    dimensions.heightIn == null ? null : Math.round(dimensions.heightIn);
  if (base) {
    if (roundedWidth != null && roundedWidth > base.widthIn) {
      issues.push(
        `The equipment is wider than the selected shelf (${roundedWidth}\" equipment; ${base.widthIn}\" shelf).`
      );
    }
    if (roundedDepth != null && roundedDepth > base.depthIn) {
      issues.push(
        `The equipment is deeper than the selected shelf (${roundedDepth}\" equipment; ${base.depthIn}\" shelf).`
      );
    }
  }

  const ventilationAllowanceIn = input.ventilationAllowanceIn ?? 0;
  if (
    roundedHeight != null &&
    roundedHeight + ventilationAllowanceIn > clearHeightIn
  ) {
    issues.push(
      `The equipment is too tall for the selected height (${roundedHeight}\" equipment plus ${ventilationAllowanceIn}\" ventilation; ${clearHeightIn}\" selected).`
    );
  }
  if (dimensions.weightKg != null) {
    notes.push(
      "Weight captured; authoritative HRS load-range validation is pending technical data."
    );
  }

  const missingDimensions = [
    dimensions.widthIn,
    dimensions.depthIn,
    dimensions.heightIn,
  ].some(value => value == null);
  if (missingDimensions)
    notes.push(
      "Some dimensions are missing, so fit cannot be fully confirmed."
    );

  if (issues.length) return { status: "does_not_fit", issues, notes };
  if (missingDimensions) return { status: "review", issues, notes };
  return { status: "fits", issues, notes };
}
